import enAccount from '../locales/en/account.json' with { type: 'json' };
import enDashboard from '../locales/en/dashboard.json' with { type: 'json' };
import enEmergency from '../locales/en/emergency.json' with { type: 'json' };
import enErrors from '../locales/en/errors.json' with { type: 'json' };
import enWidget from '../locales/en/widget.json' with { type: 'json' };
import enWidgetCore from '../locales/en/widget-core.json' with { type: 'json' };
import itAccount from '../locales/it/account.json' with { type: 'json' };
import itDashboard from '../locales/it/dashboard.json' with { type: 'json' };
import itEmergency from '../locales/it/emergency.json' with { type: 'json' };
import itErrors from '../locales/it/errors.json' with { type: 'json' };
import itWidget from '../locales/it/widget.json' with { type: 'json' };
import itWidgetCore from '../locales/it/widget-core.json' with { type: 'json' };

/**
 * Traduzioni.
 *
 * Nessuna stringa rivolta a una persona sta nel codice: né nei componenti, né
 * nelle risposte dell'API, che restituiscono una **chiave** e la lasciano
 * tradurre a chi la mostra (sezione 4).
 *
 * Italiano e inglese al lancio. Le chiavi sono in inglese e descrivono la
 * funzione, non il testo: `session.closed.title`, non `siamo_chiusi`.
 */

export const SUPPORTED_LOCALES = ['it', 'en'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'it';

/** Lingue che si scrivono da destra a sinistra. Nessuna ancora, ma il percorso esiste. */
const RTL_LOCALES = new Set<string>(['ar', 'he', 'fa', 'ur']);

export function directionFor(locale: string): 'ltr' | 'rtl' {
  return RTL_LOCALES.has(locale.split('-')[0] ?? '') ? 'rtl' : 'ltr';
}

/**
 * I namespace, e chi li scarica.
 *
 * Non è una classificazione tematica: è **il peso sul telefono di chi visita il
 * sito di un ente**. Lo snippet e il pannello si portano dietro solo `widget` e
 * `emergency`, e nient'altro può entrarci senza che qualcuno lo paghi.
 *
 * `account` è nato da un limite superato: le stringhe di registrazione, chat e
 * video hanno spinto lo snippet oltre i 30 KB della sezione 4, e la build si è
 * fermata — come deve. Quelle frasi le legge solo chi apre la pagina intera di
 * video e chat, cioè chi ha già deciso di registrarsi: non c'è motivo perché
 * viaggino fino a chi apre il sito per leggere gli orari.
 *
 * `widget` è **due file**, e per la stessa ragione portata un passo più in là.
 * `widget-core.json` sono le undici stringhe che disegna lo snippet stesso —
 * l'etichetta del lanciatore e il pannello di riserva — e `widget.json` le
 * altre novantatré, che le disegna il pannello dentro l'iframe. Qui si vedono
 * uniti perché per il server e per la console sono un namespace solo; a
 * dividerli è `w.js`, dove i dizionari completi pesavano il 44% del file
 * (`apps/snippet/src/shared/i18n-loader.ts`). Una chiave nuova va in
 * `widget.json`, salvo che la usi lo snippet.
 */
export const NAMESPACES = ['errors', 'emergency', 'dashboard', 'widget', 'account'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const resources: Record<Locale, Record<Namespace, Record<string, string>>> = {
  it: {
    errors: itErrors,
    emergency: itEmergency,
    dashboard: itDashboard,
    widget: { ...itWidgetCore, ...itWidget },
    account: itAccount,
  },
  en: {
    errors: enErrors,
    emergency: enEmergency,
    dashboard: enDashboard,
    widget: { ...enWidgetCore, ...enWidget },
    account: enAccount,
  },
};

export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Sceglie la lingua migliore fra quelle richieste e quelle disponibili.
 * Usata per l'header `Accept-Language` e per le lingue attive di un ente.
 */
export function negotiateLocale(
  requested: readonly string[],
  available: readonly string[] = SUPPORTED_LOCALES,
  fallback: string = DEFAULT_LOCALE,
): string {
  for (const candidate of requested) {
    const normalized = candidate.trim().toLowerCase();
    if (available.includes(normalized)) return normalized;
    const base = normalized.split('-')[0];
    if (base && available.includes(base)) return base;
  }
  return available.includes(fallback) ? fallback : (available[0] ?? fallback);
}

/** Analizza un header `Accept-Language` in ordine di preferenza. */
export function parseAcceptLanguage(header: string | undefined): string[] {
  if (!header) return [];
  return header
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.split(';');
      const quality = params.map((param) => /^\s*q=([\d.]+)\s*$/.exec(param)).find(Boolean)?.[1];
      return { tag: (tag ?? '').trim().toLowerCase(), quality: Number(quality ?? '1') };
    })
    .filter((entry) => entry.tag.length > 0)
    .sort((a, b) => b.quality - a.quality)
    .map((entry) => entry.tag);
}

/**
 * Traduzione lato server, per i pochi casi in cui il testo non può essere
 * tradotto dal client (registri, email). L'interpolazione è `{{nome}}`.
 */
export function translate(
  locale: string,
  key: string,
  variables: Record<string, string | number> = {},
): string {
  const [namespace, ...rest] = key.split('.');
  const path = rest.join('.');
  const chosen: Locale = isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;
  const bundle = resources[chosen][(namespace ?? '') as Namespace];
  const template =
    bundle?.[path] ?? resources[DEFAULT_LOCALE][(namespace ?? '') as Namespace]?.[path];
  if (!template) return key;
  return template.replace(/\{\{(\w+)\}\}/g, (_match, name: string) =>
    String(variables[name] ?? `{{${name}}}`),
  );
}
